import { mergeTicket, resolveConflict, validateTicketGraph, type MergeResult } from '../domain/merge';
import type { CoupleSpace, Ticket } from '../domain/types';
import {
  requestResult,
  runTransaction,
  STORE_NAMES,
  type ImportMarker,
  type StoredImage,
} from './db';

const QUOTA_MESSAGE = '本机存储空间不足，文字单据尚未丢失；请先导出备份或删除本机图片';

export class StorageQuotaError extends Error {
  readonly code = 'STORAGE_QUOTA';

  constructor(message = QUOTA_MESSAGE) {
    super(message);
    this.name = 'StorageQuotaError';
  }
}

export interface AppRepositoryContract {
  getSpace(): Promise<CoupleSpace | null>;
  saveSpace(space: CoupleSpace): Promise<void>;
  recordPartnerReceipt(coupleId: string, partnerName: string, partnerAvatar: string, receivedAt: string): Promise<CoupleSpace>;
  getTicket(id: string): Promise<Ticket | null>;
  listTickets(): Promise<Ticket[]>;
  saveTicket(ticket: Ticket, importId?: string): Promise<void>;
  saveTicketWithImage(ticket: Ticket, imageId: string, blob: Blob): Promise<void>;
  importTicket(ticket: Ticket, importId: string): Promise<MergeResult>;
  deleteTicket(id: string): Promise<void>;
  deleteTicketWithImage(id: string): Promise<void>;
  putImage(id: string, blob: Blob): Promise<void>;
  getImage(id: string): Promise<Blob | null>;
  replaceWithFreshSpace(space: CoupleSpace): Promise<void>;
  clearAll(): Promise<void>;
}

export interface BackupSnapshot {
  space: CoupleSpace;
  tickets: Ticket[];
  images: Map<string, Blob>;
}

export interface BackupImportResult {
  applied: number;
  duplicates: number;
  stale: number;
  conflicts: number;
  images: number;
}

export interface BackupConflict {
  id: string;
  local: Ticket;
  incoming: Ticket;
}

export interface BackupRepositoryContract extends AppRepositoryContract {
  importBackupSnapshot(snapshot: BackupSnapshot): Promise<BackupImportResult>;
  listBackupConflicts(): Promise<BackupConflict[]>;
  resolveBackupConflict(id: string, keep: 'local' | 'incoming', actor: CoupleSpace['memberId'], at: string): Promise<Ticket>;
}

export class AppRepository implements BackupRepositoryContract {
  constructor(private readonly factory: IDBFactory = indexedDB) {}

  async getSpace(): Promise<CoupleSpace | null> {
    return this.withStorageErrors(() => runTransaction(this.factory, 'space', 'readonly', async (transaction) => {
      const spaces = await requestResult(transaction.objectStore('space').getAll() as IDBRequest<CoupleSpace[]>);
      return spaces[0] ?? null;
    }));
  }

  async saveSpace(space: CoupleSpace): Promise<void> {
    await this.withStorageErrors(() => runTransaction(this.factory, 'space', 'readwrite', async (transaction) => {
      const store = transaction.objectStore('space');
      await requestResult(store.clear());
      await requestResult(store.put(space));
    }));
  }

  async recordPartnerReceipt(
    coupleId: string,
    partnerName: string,
    partnerAvatar: string,
    receivedAt: string,
  ): Promise<CoupleSpace> {
    requireId(coupleId, 'couple id');
    return this.withStorageErrors(() => runTransaction(this.factory, 'space', 'readwrite', async (transaction) => {
      const store = transaction.objectStore('space');
      const spaces = await requestResult(store.getAll() as IDBRequest<CoupleSpace[]>);
      const current = spaces[0];
      if (!current || current.coupleId !== coupleId) throw new Error('pairing receipt belongs to a different space');
      if (current.peerReadyAt) throw new Error('pairing receipt is already consumed');
      const updated: CoupleSpace = { ...current, partnerName, partnerAvatar, peerReadyAt: receivedAt };
      await requestResult(store.put(updated));
      return updated;
    }));
  }

  async getTicket(id: string): Promise<Ticket | null> {
    return this.withStorageErrors(() => runTransaction(this.factory, 'tickets', 'readonly', async (transaction) => {
      const ticket = await requestResult(transaction.objectStore('tickets').get(id) as IDBRequest<Ticket | undefined>);
      return ticket ?? null;
    }));
  }

  async listTickets(): Promise<Ticket[]> {
    return this.withStorageErrors(() => runTransaction(this.factory, 'tickets', 'readonly', (transaction) => (
      requestResult(transaction.objectStore('tickets').getAll() as IDBRequest<Ticket[]>)
    )));
  }

  async saveTicket(ticket: Ticket, importId?: string): Promise<void> {
    validateTicketGraph(ticket);
    if (importId !== undefined) requireId(importId, 'import id');
    const stores = importId === undefined ? ['tickets'] as const : ['tickets', 'imports'] as const;
    await this.withStorageErrors(() => runTransaction(this.factory, [...stores], 'readwrite', async (transaction) => {
      const imports = importId === undefined ? null : transaction.objectStore('imports');
      if (imports && importId !== undefined && await requestResult(imports.get(importId)) !== undefined) {
        throw new Error('这条冲突已经处理，请重新打开链接');
      }
      await requestResult(transaction.objectStore('tickets').put(ticket));
      if (imports && importId !== undefined) {
        await requestResult(imports.add({ id: importId } satisfies ImportMarker));
      }
    }));
  }

  async saveTicketWithImage(ticket: Ticket, imageId: string, blob: Blob): Promise<void> {
    validateTicketGraph(ticket);
    requireId(imageId, 'image id');
    if (ticket.imageId !== imageId) throw new TypeError('ticket image id must match stored image id');
    if (!(blob instanceof Blob)) throw new TypeError('image must be a Blob');
    const image = await storedImage(imageId, blob);
    await this.withStorageErrors(() => runTransaction(this.factory, ['tickets', 'images'], 'readwrite', async (transaction) => {
      await requestResult(transaction.objectStore('images').put(image));
      await requestResult(transaction.objectStore('tickets').put(ticket));
    }));
  }

  async importTicket(ticket: Ticket, importId: string): Promise<MergeResult> {
    validateTicketGraph(ticket);
    requireId(importId, 'import id');
    return this.withStorageErrors(() => runTransaction(this.factory, ['tickets', 'imports'], 'readwrite', async (transaction) => {
      const imports = transaction.objectStore('imports');
      const previousMarker = await requestResult(imports.get(importId));
      if (previousMarker !== undefined) return { kind: 'duplicate' };

      const tickets = transaction.objectStore('tickets');
      const local = await requestResult(tickets.get(ticket.id) as IDBRequest<Ticket | undefined>);
      const result: MergeResult = local === undefined ? { kind: 'applied', ticket } : mergeTicket(local, ticket);
      if (result.kind === 'applied') await requestResult(tickets.put(result.ticket));
      if (result.kind !== 'conflict') {
        await requestResult(imports.put({ id: importId } satisfies ImportMarker));
      }
      return result;
    }));
  }

  async deleteTicket(id: string): Promise<void> {
    requireId(id, 'ticket id');
    await this.withStorageErrors(() => runTransaction(this.factory, 'tickets', 'readwrite', async (transaction) => {
      await requestResult(transaction.objectStore('tickets').delete(id));
    }));
  }

  async deleteTicketWithImage(id: string): Promise<void> {
    requireId(id, 'ticket id');
    await this.withStorageErrors(() => runTransaction(this.factory, ['tickets', 'images'], 'readwrite', async (transaction) => {
      const tickets = transaction.objectStore('tickets');
      const ticket = await requestResult(tickets.get(id) as IDBRequest<Ticket | undefined>);
      if (ticket?.imageId) await requestResult(transaction.objectStore('images').delete(ticket.imageId));
      await requestResult(tickets.delete(id));
    }));
  }

  async putImage(id: string, blob: Blob): Promise<void> {
    requireId(id, 'image id');
    if (!(blob instanceof Blob)) throw new TypeError('image must be a Blob');
    const image = await storedImage(id, blob);
    await this.withStorageErrors(() => runTransaction(this.factory, 'images', 'readwrite', async (transaction) => {
      await requestResult(transaction.objectStore('images').put(image));
    }));
  }

  async getImage(id: string): Promise<Blob | null> {
    requireId(id, 'image id');
    return this.withStorageErrors(() => runTransaction(this.factory, 'images', 'readonly', async (transaction) => {
      const image = await requestResult(transaction.objectStore('images').get(id) as IDBRequest<StoredImage | undefined>);
      return image ? imageBlob(image) : null;
    }));
  }

  async replaceWithFreshSpace(space: CoupleSpace): Promise<void> {
    await this.withStorageErrors(() => runTransaction(this.factory, [...STORE_NAMES], 'readwrite', async (transaction) => {
      for (const storeName of STORE_NAMES) await requestResult(transaction.objectStore(storeName).clear());
      await requestResult(transaction.objectStore('space').put(space));
    }));
  }

  async clearAll(): Promise<void> {
    await this.withStorageErrors(() => runTransaction(this.factory, [...STORE_NAMES], 'readwrite', async (transaction) => {
      for (const storeName of STORE_NAMES) await requestResult(transaction.objectStore(storeName).clear());
    }));
  }

  async importBackupSnapshot(snapshot: BackupSnapshot): Promise<BackupImportResult> {
    for (const ticket of snapshot.tickets) validateTicketGraph(ticket);
    for (const [id, blob] of snapshot.images) {
      requireId(id, 'image id');
      if (!(blob instanceof Blob)) throw new TypeError('image must be a Blob');
    }
    const storedImages = new Map<string, StoredImage>();
    for (const [id, blob] of snapshot.images) storedImages.set(id, await storedImage(id, blob));

    return this.withStorageErrors(() => runTransaction(
      this.factory,
      ['space', 'tickets', 'images', 'backupConflicts'],
      'readwrite',
      async (transaction) => {
        const spaces = transaction.objectStore('space');
        const existingSpaces = await requestResult(spaces.getAll() as IDBRequest<CoupleSpace[]>);
        const existingSpace = existingSpaces[0];
        if (existingSpace && existingSpace.coupleId !== snapshot.space.coupleId) {
          throw new Error('backup belongs to a different couple space');
        }
        if (!existingSpace) await requestResult(spaces.put(snapshot.space));

        const result: BackupImportResult = {
          applied: 0,
          duplicates: 0,
          stale: 0,
          conflicts: 0,
          images: 0,
        };
        const tickets = transaction.objectStore('tickets');
        const conflicts = transaction.objectStore('backupConflicts');
        const referencedImportedImages = new Set<string>();
        for (const incoming of snapshot.tickets) {
          const local = await requestResult(tickets.get(incoming.id) as IDBRequest<Ticket | undefined>);
          const merge: MergeResult = local === undefined ? { kind: 'applied', ticket: incoming } : mergeTicket(local, incoming);
          if (merge.kind === 'applied') {
            await requestResult(tickets.put(merge.ticket));
            result.applied += 1;
            if (merge.ticket.imageId && snapshot.images.has(merge.ticket.imageId)) {
              referencedImportedImages.add(merge.ticket.imageId);
            }
          } else {
            result[merge.kind === 'duplicate' ? 'duplicates' : merge.kind === 'stale' ? 'stale' : 'conflicts'] += 1;
            if (merge.kind === 'conflict') {
              const conflict: BackupConflict = {
                id: backupConflictId(merge.local, merge.incoming), local: merge.local, incoming: merge.incoming,
              };
              await requestResult(conflicts.put(conflict));
              if (merge.incoming.imageId && snapshot.images.has(merge.incoming.imageId)) {
                referencedImportedImages.add(merge.incoming.imageId);
              }
            }
          }
        }

        const images = transaction.objectStore('images');
        for (const id of referencedImportedImages) {
          const existing = await requestResult(images.get(id));
          if (existing !== undefined) continue;
          await requestResult(images.put(storedImages.get(id)!));
          result.images += 1;
        }
        return result;
      },
    ));
  }

  async listBackupConflicts(): Promise<BackupConflict[]> {
    return this.withStorageErrors(() => runTransaction(this.factory, 'backupConflicts', 'readonly', (transaction) => (
      requestResult(transaction.objectStore('backupConflicts').getAll() as IDBRequest<BackupConflict[]>)
    )));
  }

  async resolveBackupConflict(
    id: string,
    keep: 'local' | 'incoming',
    actor: CoupleSpace['memberId'],
    at: string,
  ): Promise<Ticket> {
    requireId(id, 'backup conflict id');
    return this.withStorageErrors(() => runTransaction(this.factory, ['tickets', 'backupConflicts'], 'readwrite', async (transaction) => {
      const conflicts = transaction.objectStore('backupConflicts');
      const conflict = await requestResult(conflicts.get(id) as IDBRequest<BackupConflict | undefined>);
      if (!conflict) throw new Error('backup conflict was not found');
      const ticket = resolveConflict({ kind: 'conflict', local: conflict.local, incoming: conflict.incoming }, keep, actor, at, crypto.randomUUID());
      await requestResult(transaction.objectStore('tickets').put(ticket));
      await requestResult(conflicts.delete(id));
      return ticket;
    }));
  }

  private async withStorageErrors<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (isQuotaError(error)) throw new StorageQuotaError();
      throw error;
    }
  }
}

async function storedImage(id: string, blob: Blob): Promise<StoredImage> {
  return { id, bytes: await blob.arrayBuffer(), type: blob.type };
}

function imageBlob(image: StoredImage): Blob {
  return 'blob' in image ? image.blob : new Blob([image.bytes], { type: image.type });
}

function requireId(value: string, label: string): void {
  if (value.trim().length === 0) throw new TypeError(`${label} must not be empty`);
}

function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'QuotaExceededError';
}

function backupConflictId(local: Ticket, incoming: Ticket): string {
  return `backup:${local.id}:${local.headEventId}:${incoming.headEventId}`;
}
